import { useQuery } from "react-query";
import { useNavigate } from "react-router";
import { getDashboard } from "../../api/monitoring";
import AlertsTable from "./AlertsTable";
import { naira } from "./shared";

const Card = ({ title, value, sub, tone, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-white rounded-lg px-[24px] py-[16px] ${onClick ? "cursor-pointer hover:shadow" : ""}`}
  >
    <div className="text-[12px] text-[#00000080]">{title}</div>
    <div className={`text-[24px] font-bold mt-1 ${tone || "text-[#14142B]"}`}>{value}</div>
    {sub && <div className="text-[12px] text-[#00000080] mt-1">{sub}</div>}
  </div>
);

const Overview = () => {
  const navigate = useNavigate();
  const { data } = useQuery("monitoring-dashboard", getDashboard, { refetchInterval: 30000 });
  const o = data?.openAlerts || {};
  const max = Math.max(1, ...(data?.alertsLast7Days || []).map((d) => d.count));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-5 gap-4">
        <Card
          title="Open alerts"
          value={o.total ?? 0}
          sub={`${o.high || 0} high · ${o.medium || 0} medium · ${o.low || 0} low`}
          onClick={() => navigate("/monitoring/alerts")}
        />
        <Card title="Overdue" value={data?.overdueAlerts ?? 0} tone={data?.overdueAlerts ? "text-[#EB5757]" : undefined} />
        <Card
          title="Payments held"
          value={data?.held?.heldCount ?? 0}
          sub={data?.held ? `${naira(data.held.heldNairaAmount)}${data.held.heldCryptoCount ? ` + ${data.held.heldCryptoCount} crypto` : ""}` : "—"}
          tone={data?.held?.heldCount ? "text-[#EB5757]" : undefined}
        />
        <Card title="Open cases" value={data?.openCases ?? 0} onClick={() => navigate("/monitoring/cases")} />
        <Card title="Avg. review time (7d)" value={data?.avgReviewHours != null ? `${data.avgReviewHours}h` : "—"} />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg p-5">
          <div className="font-bold text-[14px] mb-3">Alerts in the last 7 days</div>
          <div className="flex items-end gap-2 h-[120px]">
            {(data?.alertsLast7Days || []).map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                <div className="text-[11px] text-[#00000080]">{d.count}</div>
                <div className="w-full bg-[#2121F1] rounded-t" style={{ height: `${(d.count / max) * 90}px` }} />
                <div className="text-[10px] text-[#00000080]">{d.date.slice(5)}</div>
              </div>
            ))}
            {!data?.alertsLast7Days?.length && <div className="text-[13px] text-[#00000080]">No alerts yet</div>}
          </div>
        </div>
        <div className="bg-white rounded-lg p-5 col-span-2">
          <div className="font-bold text-[14px] mb-3">Most frequent rules (7 days)</div>
          {(data?.topRules || []).map((r) => (
            <div key={r.ruleKey} className="flex justify-between text-[13px] py-1 border-b border-[#F2F2F2] last:border-0">
              <span>{r.name}</span>
              <span className="font-bold">{r.count}</span>
            </div>
          ))}
          {!data?.topRules?.length && <div className="text-[13px] text-[#00000080]">No alerts yet</div>}
        </div>
      </div>

      <div className="bg-white rounded-lg p-5">
        <div className="flex justify-between items-center mb-2">
          <div className="font-bold text-[16px]">Needs attention</div>
          <button type="button" className="text-[#4A4DE6] text-[13px]" onClick={() => navigate("/monitoring/alerts")}>
            View all alerts
          </button>
        </div>
        <AlertsTable compact />
      </div>
    </div>
  );
};

export default Overview;
